<script setup lang="ts">
import type { IDepositRate } from '~/components/entity/deposit'
import { CurrencyIcon } from '~/components/ui/currency-icon'

const props = defineProps<{
  rates: IDepositRate[]
}>()

const currencyList = useCurrencyStore().list

const grouped = computed(() =>
  props.rates.reduce<Record<string, IDepositRate[]>>((acc, rate) => {
    const currency = currencyList.find((i) => i.id === rate.currency_id)
    if (!currency) return acc

    const key = currency.code
    if (!acc[key]) acc[key] = []
    acc[key].push(rate)
    return acc
  }, {})
)

function formatPeriod(rate: IDepositRate): string {
  if (rate.period_from && rate.period_from !== rate.period) {
    return `${rate.period_from} - ${rate.period}`
  }
  return `${rate.period}`
}

const allPeriods = computed(() => {
  const periodsSet = new Set<string>()
  props.rates.forEach((rate) => periodsSet.add(formatPeriod(rate)))
  return Array.from(periodsSet).sort((a, b) => {
    const parse = (val: string) => parseInt(val.split(' ')[0])
    return parse(a) - parse(b)
  })
})

const rateByCodeAndPeriod = computed(() => {
  const map = new Map<string, number>()
  for (const [code, rates] of Object.entries(grouped.value)) {
    for (const r of rates) {
      map.set(`${code}:${formatPeriod(r)}`, r.rate)
    }
  }
  return map
})
</script>

<template>
  <div class="space-y-4">
    <div v-if="!Object.keys(grouped).length" class="text-gray-500">
      {{ $t('common.no_available_interest_rates') }}
    </div>

    <template v-else>
      <div class="overflow-x-auto">
        <table class="w-full min-w-fit border-separate border-spacing-0">
          <thead class="bg-gray-100 text-left font-semibold">
            <tr>
              <th class="rounded-tl-lg border-l border-t border-gray-200 px-3 py-4">
                {{ $t('common.currency') }}
              </th>
              <th
                v-for="(period, idx) in allPeriods"
                :key="period"
                :class="[
                  'whitespace-nowrap border-l border-t border-gray-200 px-3 py-4 text-center',
                  idx === allPeriods.length - 1 ? 'rounded-tr-lg border-r' : ''
                ]"
              >
                {{ period }} {{ $t('common.months_short') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(rates_data, code, rowIndex) in grouped" :key="code">
              <td
                :class="[
                  'border-b border-l border-gray-200 px-3 py-4 text-gray-600',
                  rowIndex === Object.keys(grouped).length - 1 ? 'rounded-bl-lg' : ''
                ]"
              >
                <div class="flex gap-2">
                  <CurrencyIcon
                    :code="useCurrencyProp(grouped[code][0].currency_id, 'code') || ''"
                    size="lg"
                    country
                  />{{ code }}
                </div>
              </td>
              <td
                v-for="(period, idx) in allPeriods"
                :key="period"
                :class="[
                  'border-b border-l border-gray-200 px-3 py-4 text-center',
                  idx === allPeriods.length - 1 ? 'border-r' : '',
                  rowIndex === Object.keys(grouped).length - 1 && idx === allPeriods.length - 1
                    ? 'rounded-br-lg'
                    : ''
                ]"
              >
                {{ rateByCodeAndPeriod.get(`${code}:${period}`) != null ? rateByCodeAndPeriod.get(`${code}:${period}`) + '%' : '—' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>
